import React, { useReducer } from "react";
import { useApiCache, apiDirectCall } from "customHooks/useApi";
import { useTranslation } from "react-i18next";
import { Box } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import {
    ApplicantViewContainerType,
    IApplicantViewMainState,
    IApplicantViewMainStateAction,
    IApplicantViewLocalStateData,
    ApplicantViewModulesType,
    ApplicantViewExternalData,
    ApplicantViewJobDataInsideJob
} from "./ApplicantViewTypes";
import { useAuthorization, useAuthState } from "_ReactContext/AuthContext";
import useGetMetadata from "customHooks/useGetMetadata";
import CompleoErrorMessage from "Components/CompleoErrorMessage";

const ApplicantViewContext = React.createContext<
    IApplicantViewMainState | undefined
>(undefined);
const ApplicantViewContextDispatch = React.createContext<
    React.Dispatch<IApplicantViewMainStateAction> | undefined
>(undefined);

const reducer = (
    state: IApplicantViewLocalStateData,
    action: IApplicantViewMainStateAction
): IApplicantViewLocalStateData => {
    switch (action.type) {
        case "update": {
            return { ...state, ...action.payload };
        }
    }
};

interface IApplicantViewProvider {
    applicantId: string;
    children: React.ReactNode;
    containerType: ApplicantViewContainerType;
    index: number;
    isInsideKanban: boolean;
    jobDataInsideJob?: ApplicantViewJobDataInsideJob;
    tabOpen?: string;
    openRatingOnStart?: boolean;
    module?: ApplicantViewModulesType;
    externalData?: ApplicantViewExternalData;
    customCVsData?: Compleo.IObject[];
    evaluationId?: string;
    cvToShare?: Compleo.IObject;
}

export const ApplicantViewProvider = (props: IApplicantViewProvider) => {
    const {
        applicantId,
        children,
        containerType,
        index,
        isInsideKanban,
        jobDataInsideJob,
        tabOpen,
        openRatingOnStart = false,
        module = "APPLICANTVIEW",
        externalData,
        customCVsData,
        evaluationId,
        cvToShare
    } = props;

    const { isFeatureAuthorized } = useAuthorization();
    const readOnly = !isFeatureAuthorized("EditApplicant");

    const moduleName = module;
    const [t, i18n, readyTranslation] = useTranslation(moduleName, {
        useSuspense: false
    });
    const isJobRequestOrEvaluation = module !== "APPLICANTVIEW";
    const queryString = isJobRequestOrEvaluation
        ? evaluationId
            ? `?evaluationId=${evaluationId}`
            : `?isJobRequest=true`
        : "";

    const queryKey = !externalData
        ? `/applicant/searchfulldata${queryString}`
        : `/applicant/searchfulldataexternal`;

    const [metadata] = useGetMetadata(moduleName, externalData?.companyId);

    const defaultState: IApplicantViewLocalStateData = {};

    const [localStateData, dispatchLocalStateData] = useReducer(
        reducer,
        defaultState
    );

    const { company, timeZone } = useAuthState();
    const companyId =
        company.companyId > 0
            ? company.companyId
            : externalData?.companyId || 0;

    const paramsGetApplicant = !externalData
        ? {
              id: applicantId,
              companyId: companyId
          }
        : {
              applicantId: applicantId,
              evaluationId: externalData.evaluationId
          };

    const [getApplicantReturn, getApplicant] = useApiCache(
        queryKey,
        "post",
        paramsGetApplicant,
        false
    );

    const [fetchedCustomCVsData, setFetchedCustomCVsData] = React.useState<
        Compleo.IObject[] | undefined
    >(undefined);

    React.useEffect(() => {
        if (!customCVsData && Number(companyId) > 0) {
            apiDirectCall("/customcv/getcustomcv", "post", {
                companyId: Number(companyId),
                jobId: jobDataInsideJob?.jobId
            }).then((result: any) => {
                setFetchedCustomCVsData(result.data || []);
            });
        }
    }, [customCVsData, companyId, jobDataInsideJob?.jobId]);

    React.useEffect(() => {
        if (getApplicantReturn.status === "success") {
            getApplicant(paramsGetApplicant);
        }
    }, [applicantId, getApplicantReturn.status]);
    const is404error = getApplicantReturn?.exception?.response?.status === 404;

    /**
     * Return Data
     */
    const ready = getApplicantReturn.status === "success" && readyTranslation;
    const applicantData = getApplicantReturn.response?.data?.data || {};

    const returnMain: IApplicantViewMainState = {
        ready: ready,
        metadata: metadata,
        t: t,
        language: i18n.languages[0],
        applicantDataReturn: getApplicantReturn,
        containerType: containerType,
        queryKey: queryKey,
        localStateData: localStateData,
        applicantData: applicantData,
        timeZone: timeZone,
        applicantId: applicantId,
        companyId: Number(companyId),
        jobDataInsideJob: jobDataInsideJob,
        applicantIndex: index,
        isInsideKanban: isInsideKanban,
        tabOpen: tabOpen,
        openRatingOnStart: openRatingOnStart,
        externalData: externalData,
        readOnly: readOnly,
        customCVsData: customCVsData || fetchedCustomCVsData,
        evaluationId,
        cvToShare
    };
    const returnDispatch: React.Dispatch<IApplicantViewMainStateAction> = dispatchLocalStateData;

    const content = is404error ? (
        <CompleoErrorMessage
            errorTitle={t("notFound")}
            errorDescription={t("applicantNotFound")}
        />
    ) : (
        children
    );

    return (
        <ApplicantViewContext.Provider value={returnMain}>
            <ApplicantViewContextDispatch.Provider value={returnDispatch}>
                {content}
            </ApplicantViewContextDispatch.Provider>
        </ApplicantViewContext.Provider>
    );
};

const useValues = () => {
    const context = React.useContext(ApplicantViewContext);
    if (!context) {
        throw new Error(
            `useValues must be used within a ApplicantViewProvider`
        );
    }
    const returnData = context;
    return returnData;
};

const useDispatch = () => {
    const context = React.useContext(ApplicantViewContextDispatch);
    if (!context) {
        throw new Error(
            `useDispatch must be used within a ApplicantViewProvider`
        );
    }
    const returnData = context;
    return returnData;
};

const useApplicantViewContext: () => [
    IApplicantViewMainState,
    React.Dispatch<IApplicantViewMainStateAction>
] = () => {
    return [useValues(), useDispatch()];
};

export default useApplicantViewContext;
